/**
 * Whether the text-formatting bubble menu should show for a selection.
 *
 * Extracted from `<MarkdownEditor>` so it can be tested directly: the
 * component is shared by chat, card descriptions, comments and the wiki, so
 * a wrong answer here silently removes formatting from every surface at
 * once.
 */

import { NodeSelection } from '@tiptap/pm/state'
import type { Selection } from '@tiptap/pm/state'

export function shouldShowFormattingMenu(
  selection: Selection,
  hasFocus: boolean,
  isEditable: boolean,
): boolean {
  // A read-only surface still takes focus and still supports a text
  // selection, so nothing else here excludes it. TipTap's default shouldShow
  // checks editability, but a custom predicate replaces that default — so the
  // check has to be made again here or a reader gets a formatting bar whose
  // buttons genuinely mutate the document.
  if (!isEditable) return false

  const { from, to, empty } = selection
  if (empty || from === to) return false

  // A selected ATOM — an image, a video, a file chip, a work-item chip — is
  // a non-empty selection, so without this the text-formatting menu opened
  // over the media. Every button in it applies an inline mark and an atom
  // has no inline content to mark, so the whole bar was inert: bold, italic,
  // link or quote over a video did nothing at all. Hide rather than disable,
  // since no subset of it means anything here.
  if (selection instanceof NodeSelection && selection.node.isAtom) return false

  return hasFocus
}
